import React, { useState } from "react";
import { motion } from "framer-motion";
import SafeIcon from "../common/SafeIcon";
import * as FiIcons from "react-icons/fi";

const { FiMail, FiPhone, FiMapPin, FiSend, FiCheck, FiLoader } = FiIcons;

/**
 * CONTACT FORM – Improved (v2)
 * – Async submission to Formspree endpoint (no backend required).
 * – Client‑side validation & UX enhancements.
 * – reCAPTCHA v3 ready (optional).
 * – Honeypot field for extra spam protection.
 */


const FORMSPREE_ENDPOINT = "https://formspree.io/f/meozlked";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    project: "",
    message: "",
    _gotcha: "", // honeypot
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error

  // ─── Handlers ──────────────────────────────────────────────
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData._gotcha) return; // bot trap

    setStatus("submitting");
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          project: formData.project,
          message: formData.message,
        }),
      });
      if (res.ok) {
        setStatus("success");
        setFormData({
          name: "",
          email: "",
          company: "",
          phone: "",
          project: "",
          message: "",
          _gotcha: "",
        });
      } else {
        throw new Error("Network response was not ok");
      }
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  // ─── Contact panel constants ──────────────────────────────
  const contactInfo = [
    {
      icon: FiPhone,
      title: "Call Us",
      content: "+1 (813) 123‑4567",
      description: "Mon–Fri 9 a.m.‑5 p.m. EST",
    },
    {
      icon: FiMail,
      title: "Email Us",
      content: "info@greengasturbines.com",
      description: "Replies within 24 h",
    },
    {
      icon: FiMapPin,
      title: "Visit Us",
      content: "Tampa, FL",
      description: "Book a facility tour",
    },
  ];

  // ─── Render ───────────────────────────────────────────────
  return (
    <section id="quote" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-secondary-900 mb-6">
            Ready to Go Green?
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Talk to our engineers today and get a custom payback analysis for
            your green‑gas‑turbine project.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* ─── Contact Info panel ─────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">
                Get in Touch
              </h3>
              <p className="text-secondary-600 mb-8">
                Our team will respond within one business day.
              </p>
            </div>
            {contactInfo.map(({ icon, title, content, description }) => (
              <div key={title} className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-lg flex-shrink-0">
                  <SafeIcon icon={icon} className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-secondary-900 mb-1">{title}</h4>
                  <p className="text-primary-600 font-medium">{content}</p>
                  <p className="text-sm text-secondary-500">{description}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* ─── Form panel ─────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">
                Request a Consultation
              </h3>

              {status === "success" ? (
                <SuccessMessage />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6 relative">
                  {/* Honeypot field (hidden) */}
                  <input
                    type="text"
                    name="_gotcha"
                    style={{ display: "none" }}
                    value={formData._gotcha}
                    onChange={handleChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />

                  {/* Name + Email */}
                  <TwoColRow>
                    <InputField
                      label="Full Name *"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Jane Doe"
                    />
                    <InputField
                      type="email"
                      label="Email Address *"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="jane@company.com"
                    />
                  </TwoColRow>

                  {/* Company + Phone */}
                  <TwoColRow>
                    <InputField
                      label="Company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                    />
                    <InputField
                      type="tel"
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 987‑6543"
                    />
                  </TwoColRow>

                  {/* Project Type */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Project Type
                    </label>
                    <select
                      name="project"
                      value={formData.project}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select Project Type</option>
                      <option value="industrial">Industrial Application</option>
                      <option value="utility">Utility Scale</option>
                      <option value="commercial">Commercial Building</option>
                      <option value="retrofit">Existing System Retrofit</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Project Details
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Briefly describe fuel blend, capacity, timeline, etc."
                    />
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={status === "submitting"}
                    className="w-full bg-primary-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-60"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {status === "submitting" ? (
                      <SafeIcon
                        icon={FiLoader}
                        className="animate-spin h-5 w-5"
                      />
                    ) : (
                      <SafeIcon icon={FiSend} className="h-5 w-5" />
                    )}
                    <span>{status === "submitting" ? "Sending…" : "Send Inquiry"}</span>
                  </motion.button>

                  {status === "error" && (
                    <p className="text-red-600 text-sm pt-2 text-center">
                      Something went wrong. Please try again later.
                    </p>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ─── Helper Components ───────────────────────────────────────
const TwoColRow = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{children}</div>
);

function InputField({ label, name, value, onChange, placeholder, type = "text", required }) {
  return (
    <div>
      <label className="block text-sm font-medium text-secondary-700 mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
    </div>
  );
}

function SuccessMessage() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="text-center py-12"
    >
      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <SafeIcon icon={FiCheck} className="h-8 w-8 text-primary-600" />
      </div>
      <h4 className="text-xl font-semibold text-secondary-900 mb-2">Thank You!</h4>
      <p className="text-secondary-600">
        We've received your inquiry and will contact you within 24 hours.
      </p>
    </motion.div>
  );
}